import React, { useCallback, useMemo, useState } from 'react';
import { Linking, View } from 'react-native';
import FastImage from 'utils/packages/rnFastImage';
import TouchableBounce from 'utils/packages/TouchableBounce';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { CardView, CARD_VIEW_BORDER_RADIUS, rem, Spacer } from 'design-system';
import { IconEmma } from 'design-system/icons';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import links from 'utils/links';
import createStyleSheets from 'utils/createStyleSheets';
import { USE_SAFARI_VIEW_OR_CHROME_TABS_FOR_NEWS } from '../../../invest/constants';
import { isWeb } from '../../../../constants';
const IMAGE_SIZE = 64;
const IMAGE_BORDER_RADIUS = 12;
const CARD_INTERNAL_PADDING = 16;
const CARD_CONTENT_VISIBILITY_OFFSET = 24;
const LEFT_SPACING = 16;
const SPACING_BETWEEN_ITEMS = 8;
const MAX_WIDTH = 328;
const getCardWidth = (width) => width -
    rem(LEFT_SPACING +
        SPACING_BETWEEN_ITEMS +
        CARD_INTERNAL_PADDING +
        CARD_CONTENT_VISIBILITY_OFFSET);
const getCardFullWidth = (width) => width - rem(LEFT_SPACING + CARD_INTERNAL_PADDING);
const LearnArticleCard = ({ data, forList, noMarginRight, fullWidth, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { width } = useAppFrameDimensions();
    const [showIconEmma, setShowIconEmma] = useState(true);
    const onPress = useCallback(() => {
        if (USE_SAFARI_VIEW_OR_CHROME_TABS_FOR_NEWS) {
            links.openLink(data.url);
        }
        else {
            Linking.openURL(data.url);
        }
    }, [data.url]);
    const content = useMemo(() => (<View style={styles.contentContainer}>
        <View style={styles.flex}>
          <Text Text-14 numberOfLines={2}>
            {data.headline}
          </Text>
          <Spacer.H4 />
          <Text Text-12 Gray>
            {`${data.source}`}
          </Text>
        </View>
        <Spacer.W16 />
        <View style={styles.imageContainer}>
          {showIconEmma ? (<IconEmma scale={2} color={colors.elements.secondaryLight}/>) : null}
          <FastImage style={styles.image} source={{ uri: data.image }} onLoad={() => {
            setShowIconEmma(false);
        }}/>
        </View>
      </View>), [data.headline, data.source, data.image, showIconEmma, colors, styles]);
    if (isWeb) {
        return (<TouchableHighlight hoverOnLight onCard onPress={onPress}>
        <View style={styles.containerForList}>{content}</View>
      </TouchableHighlight>);
    }
    if (forList) {
        return (<TouchableBounce onPress={onPress}>
        <CardView noMarginBottom>
          <View style={styles.containerForList}>{content}</View>
        </CardView>
      </TouchableBounce>);
    }
    const cardWidth = !fullWidth
        ? Math.min(rem(MAX_WIDTH), getCardWidth(width))
        : getCardFullWidth(width);
    return (<TouchableBounce onPress={onPress} style={[
            styles.container,
            styles.touchable,
            noMarginRight && styles.noMarginRight,
            { width: cardWidth },
        ]}>
      <CardView noMarginTop noMarginBottom style={[styles.container, { width: cardWidth }]}>
        {content}
      </CardView>
    </TouchableBounce>);
};
const styleSet = createStyleSheets((colors) => ({
    touchable: {
        marginRight: rem(SPACING_BETWEEN_ITEMS),
        padding: 0,
    },
    noMarginRight: {
        marginRight: 0,
    },
    container: {
        margin: 0,
        padding: rem(CARD_INTERNAL_PADDING),
        flexDirection: 'row',
        overflow: 'visible',
        borderRadius: CARD_VIEW_BORDER_RADIUS,
    },
    containerForList: {
        margin: 0,
        padding: rem(CARD_INTERNAL_PADDING),
        flexDirection: 'row',
    },
    contentContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    imageContainer: {
        width: rem(IMAGE_SIZE),
        height: rem(IMAGE_SIZE),
        borderRadius: rem(IMAGE_BORDER_RADIUS),
        backgroundColor: colors.cards.transparentOnDark,
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        position: 'absolute',
        width: rem(IMAGE_SIZE),
        height: rem(IMAGE_SIZE),
        borderRadius: rem(IMAGE_BORDER_RADIUS),
    },
}));
export default LearnArticleCard;
